import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';
import { z } from 'zod';

/**
 * Italian address schema matching board-79's ItalianAddressInput component
 * This must match the schema used in BaseInfo.tsx for type consistency
 */
const italianAddressManualSchema = z.object({
  manual: z.literal(true),
  address: z.string(),
  city: z.string(),
  cap: z.string(),
  province: z.string()
});

const italianAddressAutoSchema = z.object({
  manual: z.literal(false),
  placeId: z.string(),
  label: z.string(),
  sessionToken: z.string().uuid()
});

const italianAddressSchema = z.union([
  italianAddressManualSchema,
  italianAddressAutoSchema
]);

/**
 * Sales point schema for onboarding
 * Includes name and address
 */
const salesPointSchema = z.object({
  name: z.string(),
  address: italianAddressSchema
});

/**
 * Onboarding data schema
 * Stores all data collected during the onboarding flow
 * All fields are optional since they're populated step by step
 */
export const onboardingDataSchema = z.object({
  // Step 1 - Basic Info
  business_name: z.string().optional(),
  hasOneSalesPoint: z.boolean().optional(),
  mainSalesPointAddress: italianAddressSchema.optional(),
  extraSalesPoints: z.array(salesPointSchema).optional(),

  // Step 2 - Category
  business_category: z.string().uuid().optional(),

  // Step 3 - Goals & Customization
  goals: z.record(z.string(), z.number()).optional(),
  brand_color: z.string().optional(),
  default_cover_image: z.string().optional(), // SVG generated background (always updated)
  cover_image: z.string().optional() // Uploaded image (if user uploads one)
});

export type OnboardingData = z.infer<typeof onboardingDataSchema>;

interface OnboardingState {
  // Current step in the onboarding flow
  currentStep: number;

  // Onboarding data
  data: OnboardingData;

  // Actions
  setStep: (step: number) => void;
  nextStep: () => void;
  previousStep: () => void;
  updateData: (updates: Partial<OnboardingData>) => void;
  resetOnboarding: () => void;
}

export const useOnboardingStore = create<OnboardingState>()(
  persist(
    (set) => ({
      currentStep: 0,
      data: {},

      setStep: (step) => {
        set({ currentStep: step });
      },

      nextStep: () => {
        set((state) => ({ currentStep: state.currentStep + 1 }));
      },

      previousStep: () => {
        set((state) => ({
          currentStep: Math.max(0, state.currentStep - 1)
        }));
      },

      updateData: (updates) => {
        set((state) => ({
          data: {
            ...state.data,
            ...updates
          }
        }));
      },

      resetOnboarding: () => {
        set({
          currentStep: 0,
          data: {}
        });
      }
    }),
    {
      name: 'onboarding-storage',
      storage: createJSONStorage(() => localStorage)
    }
  )
);
